import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Badge,
} from "react-bootstrap";
import { getInvoiceCaseNotes } from "../../../../services/InvoiceAPI";
import { INVOICE_NOTES_INFO_HEADER } from "../../ManageInvoice/actions/constants";
import FloatingTableModal from "../../../../common/FloatingTableModal";
import Loader from "../../../../common/Loader";
import InvoiceReportsLogs from "./InvoiceReportsLogs";
import { dateTimeFormatted } from "../../../../utils/dateUtils";
import TruncateWithTooltip from "../../../../common/TruncateWithTooltip";
import { INVOICE_STATUS } from "../../../../common/Constant";

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    style?: any;
    isNumeric?: boolean;
  }[];
  filterPayload?: any;
  activeTab: string;
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  onRowSelect?: (data: {
    selectedRows: any[];
    selectedInvoiceId?: any;
    nestedRows?: any;
  }) => void;
  selectedRowIds?: string[];
  disabled?: boolean;
  isLogTable?: boolean;
  getInvoiceByClientQme?: (payload: any) => Promise<any[]>;
  getChildUser?: (payload: any) => Promise<any[]>;
  clientInvoiceData?: any[];
  resetTrigger?: boolean;
  isLoader?: boolean;
  activeCardDisable: boolean;
}

interface RowLevel {
  id: string;
  level: number;
  isExpanded: boolean;
  data: any;
  children?: RowLevel[];
  isLoading?: boolean;
  isLeaf?: boolean;
  parentId?: string;
  mainParentId?: string;
}

interface SortConfig {
  key: string;
  direction: "ASC" | "DESC";
}

interface AggregatedStats {
  noOfPages: number;
  noOfLines: number;
  amount: number;
  totalAmount: number;
}

const InvoiceReportDataTable: React.FC<DataTableProps> = ({
  headers,
  filterPayload,
  activeTab,
  rows = [],
  onAction,
  onSortAction,
  onRowSelect,
  selectedRowIds = [],
  disabled = false,
  isLogTable = false,
  getInvoiceByClientQme,
  getChildUser,
  clientInvoiceData = [],
  resetTrigger = false,
  isLoader = false,
  activeCardDisable = false,
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [nestedRows, setNestedRows] = useState<RowLevel[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>(
    selectedRowIds || [],
  );
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: "ASC",
  });
  const [selectionUpdated, setSelectionUpdated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [invoiceID, setInvoiceID] = useState("");
  const [modalDetails, setModalDetails] = useState<any[]>([]);
  const [modalNotesData, setModalNotesData] = useState<any[]>([]);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalPos, setModalPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);

  const isInitialMount = useRef(true);
  const prevClientInvoiceData = useRef<any[]>([]);
  const prevSelectedRowIds = useRef<string[]>(selectedRowIds || []);
  const nestedRowsRef = useRef<RowLevel[]>([]);

  // Store the selected invoice ID (Level 0 invoice ID) when any row is selected
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(isLoader);

  // Update ref when nestedRows changes
  useEffect(() => {
    nestedRowsRef.current = nestedRows;
  }, [nestedRows]);

  const findRow = useCallback(
    (rows: RowLevel[], targetId: string): RowLevel | null => {
      for (const row of rows) {
        if (row.id === targetId) return row;
        if (row.children) {
          const found = findRow(row.children, targetId);
          if (found) return found;
        }
      }
      return null;
    },
    [],
  );

  // Helper function to get the Level 0 invoice ID for any row
  const getLevel0InvoiceId = useCallback(
    (row: RowLevel): number | null => {
      // If it's a Level 0 row
      if (row.level === 0) {
        return row.data?.invoiceId || null;
      }

      // For Level 1 and 2, trace back to parent Level 0
      let currentRow = row;
      while (currentRow.parentId) {
        const parentRow = findRow(nestedRowsRef.current, currentRow.parentId);
        if (parentRow) {
          if (parentRow.level === 0) {
            return parentRow.data?.invoiceId || null;
          }
          currentRow = parentRow;
        } else {
          break;
        }
      }

      // If we can't find the parent, check mainParentId for Level 2 rows
      if (row.mainParentId) {
        const parentRow = findRow(nestedRowsRef.current, row.mainParentId);
        return parentRow?.data?.invoiceId || null;
      }

      return null;
    },
    [findRow],
  );

  // Helper function to check if a row is under the selected invoice
  const isRowUnderSelectedInvoice = useCallback(
    (row: RowLevel): boolean => {
      if (!selectedInvoiceId) return true; // If no invoice selected, all are enabled

      const rowInvoiceId = getLevel0InvoiceId(row);
      return rowInvoiceId === selectedInvoiceId;
    },
    [selectedInvoiceId, getLevel0InvoiceId],
  );

  const isRowEligibleForSelection = useCallback((rowData: any): boolean => {
    return !!rowData.invoiceStatus;
  }, []);

  const getAllEligibleLeafIds = useCallback(
    (row: RowLevel): string[] => {
      if (row.level === 2 || row.isLeaf) {
        return isRowEligibleForSelection(row.data) ? [row.id] : [];
      }

      let leafIds: string[] = [];
      if (row.children && row.children.length > 0) {
        row.children.forEach((child) => {
          leafIds = [...leafIds, ...getAllEligibleLeafIds(child)];
        });
      }
      return leafIds;
    },
    [isRowEligibleForSelection],
  );

  const toggleRowExpansion = useCallback(
    (rows: RowLevel[], rowId: string, isLoading?: boolean): RowLevel[] => {
      return rows.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            isExpanded: isLoading ? row.isExpanded : !row.isExpanded,
            ...(isLoading !== undefined && { isLoading }),
          };
        }
        if (row.children) {
          return {
            ...row,
            children: toggleRowExpansion(row.children, rowId, isLoading),
          };
        }
        return row;
      });
    },
    [],
  );

  const updateRowChildren = useCallback(
    (rows: RowLevel[], parentId: string, children: RowLevel[]): RowLevel[] => {
      return rows.map((row) => {
        if (row.id === parentId) {
          return {
            ...row,
            children,
            isLoading: false,
            isExpanded: children?.length > 0,
          };
        }
        if (row.children) {
          return {
            ...row,
            children: updateRowChildren(row.children, parentId, children),
          };
        }
        return row;
      });
    },
    [],
  );

  const handleToggleExpand = useCallback(
    async (rowId: string, level: number, rowData: any, forceExpand = false) => {
      const row = findRow(nestedRows, rowId);
      if (!row) return;

      // If already expanded and not forcing, collapse
      if (row.isExpanded && !forceExpand) {
        setNestedRows((prev) => toggleRowExpansion(prev, rowId, false));
        return;
      }

      if (row.isLoading) return;

      const hasFetchedBefore = row.children !== undefined;
      // For level 0 and 1 rows that haven't been expanded yet, fetch children
      if ((level === 0 || level === 1) && !hasFetchedBefore) {
        setNestedRows((prev) => toggleRowExpansion(prev, rowId, true));

        try {
          let children: RowLevel[] = [];

          if (level === 0) {
            // Level 0 (Client) -> Fetch QME Doctors
            const parts = rowId.split("-");
            const clientId = parseInt(parts[1]);
            const invoiceId = parseInt(parts[3]);

            const clientPayload = {
              ...filterPayload,
              clientId: clientId,
              invoiceId: invoiceId,
            };

            if (getInvoiceByClientQme) {
              const qmeDoctors = await getInvoiceByClientQme(clientPayload);

              children = qmeDoctors.map((qme) => ({
                id: `qme-${qme.qmeDoctorContactId}-${invoiceId}`,
                level: 1,
                isExpanded: false,
                isLoading: false,
                parentId: rowId,
                data: {
                  ...qme,
                  patientName: qme.qmeDoctorName,
                  noOfPages: qme.grandTotalNoOfPages,
                  noOfLines: qme.grandTotalNoOfLines,
                  amount: qme.grandTotalAmount,
                  totalAmount: qme.grandTotalAmount,
                  countOfCases: qme.countOfCases,
                  clientId: qme.clientId,
                  qmeDoctorContactId: qme.qmeDoctorContactId,
                  invoiceId: invoiceId,
                },
              }));
            }
          } else if (level === 1) {
            // Level 1 (QME Doctor) -> Fetch User Details
            const parts = rowId.split("-");
            const qmeDoctorContactId = parts[1];
            const invoiceId = parseInt(parts[2]);

            const paramNameMap: Record<string, string> = {
              Client: "qmeDoctorContactId",
              Role: "billingForUserRole",
              User: "userId",
            };

            const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
            const payloadCase = {
              ...filterPayload,
              clientId: row.data?.clientId,
              invoiceId: invoiceId,
              [paramName]: qmeDoctorContactId,
            };

            if (getChildUser) {
              const userDetails = await getChildUser(payloadCase);
              const level0Row = findRow(nestedRowsRef.current, row.parentId!);
              const level0Id =
                level0Row?.id || row.parentId?.split("-").slice(0, 4).join("-");

              children = userDetails.map((user, index) => ({
                id: `user-${qmeDoctorContactId}-${invoiceId}-${user.invoiceLineItemId || index}`,
                level: 2,
                isExpanded: false,
                isLoading: false,
                isLeaf: true,
                parentId: rowId,
                data: {
                  ...user,
                  patientName: user.patientName || "",
                  taskName: user.taskName || "",
                  noOfPages: user.noOfPages || "0",
                  noOfLines: user.noOfLines || "0",
                  rateType: user.rateType || "",
                  dateUploaded: user.dateUploaded || "",
                  rate: user.rate,
                  amount: user.amount,
                  invoiceStatus: user.invoiceStatus,
                  action: "View",
                  clientId: user.clientId,
                  qmeDoctorContactId: qmeDoctorContactId,
                  invoiceId: invoiceId,
                  parentId: rowId,
                  mainParentId: level0Id,
                },
              }));
            }
          }

          setNestedRows((prev) => updateRowChildren(prev, rowId, children));
        } catch (error) {
          console.error("Failed to fetch child data:", error);
          setNestedRows((prev) => toggleRowExpansion(prev, rowId, false));
        }
      } else {
        // Just toggle expansion if children are already loaded
        setNestedRows((prev) => toggleRowExpansion(prev, rowId));
      }
    },
    [
      nestedRows,
      findRow,
      toggleRowExpansion,
      updateRowChildren,
      getInvoiceByClientQme,
      getChildUser,
      filterPayload,
      activeTab,
    ],
  );

  // FIXED: Function to check if all eligible children are selected
  const areAllEligibleChildrenSelected = useCallback(
    (row: RowLevel): boolean => {
      // Get all eligible leaf IDs recursively
      const getAllEligibleLeafIds = (currentRow: RowLevel): string[] => {
        let leafIds: string[] = [];

        const traverse = (r: RowLevel) => {
          if (r.level === 2 || r.isLeaf) {
            if (isRowEligibleForSelection(r.data)) {
              leafIds.push(r.id);
            }
          }
          if (r.children && r.children?.length > 0) {
            r.children.forEach((child) => traverse(child));
          }
        };

        traverse(currentRow);
        return leafIds;
      };

      const eligibleLeafIds = getAllEligibleLeafIds(row);

      // If no eligible leaves, return false
      if (eligibleLeafIds?.length === 0) return false;

      // Check if ALL eligible leaves are selected
      return eligibleLeafIds.every((id) => selectedRows.includes(id));
    },
    [selectedRows, isRowEligibleForSelection],
  );

  // FIXED: Function to check if some (but not all) eligible children are selected
  const isSomeChildrenSelected = useCallback(
    (row: RowLevel): boolean => {
      const eligibleLeafIds = getAllEligibleLeafIds(row);
      if (eligibleLeafIds.length === 0) return false;

      const someSelected = eligibleLeafIds.some((id) =>
        selectedRows.includes(id),
      );
      const allSelected = eligibleLeafIds.every((id) =>
        selectedRows.includes(id),
      );

      return someSelected && !allSelected;
    },
    [selectedRows, getAllEligibleLeafIds],
  );

  // FIXED: Main header checkbox - Select/Deselect ALL
  const handleSelectAll = useCallback(async () => {
    const expandAllRows = async (rows: RowLevel[]): Promise<RowLevel[]> => {
      const expandedRows = [...rows];

      for (let i = 0; i < expandedRows?.length; i++) {
        const row = expandedRows[i];

        // If this is a parent row (level 0 or 1) that's not expanded or has no children
        if (
          row.level < 2 &&
          (!row.isExpanded || !row.children || row.children?.length === 0)
        ) {
          expandedRows[i] = { ...row, isLoading: true };

          try {
            let children: RowLevel[] = [];

            if (row.level === 0) {
              // Level 0 (Client) -> Fetch QME Doctors
              const parts = row.id.split("-");
              const clientId = parseInt(parts[1]);
              const invoiceId = parseInt(parts[3]);

              const clientPayload = {
                ...filterPayload,
                clientId: clientId,
                invoiceId: invoiceId,
              };

              if (getInvoiceByClientQme) {
                const qmeDoctors = await getInvoiceByClientQme(clientPayload);
                children = qmeDoctors.map((qme) => ({
                  id: `qme-${qme.qmeDoctorContactId}-${invoiceId}`,
                  level: 1,
                  isExpanded: false,
                  isLoading: false,
                  parentId: row.id,
                  data: {
                    ...qme,
                    patientName: qme.qmeDoctorName,
                    noOfPages: qme.grandTotalNoOfPages,
                    noOfLines: qme.grandTotalNoOfLines,
                    amount: qme.grandTotalAmount,
                    totalAmount: qme.grandTotalAmount,
                    countOfCases: qme.countOfCases,
                    clientId: qme.clientId,
                    qmeDoctorContactId: qme.qmeDoctorContactId,
                    invoiceId: invoiceId,
                  },
                }));
              }
            } else if (row.level === 1) {
              // Level 1 (QME Doctor) -> Fetch User Details
              const parts = row.id.split("-");
              const qmeDoctorContactId = parts[1];
              const invoiceId = parseInt(parts[2]);

              const paramNameMap: Record<string, string> = {
                Client: "qmeDoctorContactId",
                Role: "billingForUserRole",
                User: "userId",
              };

              const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
              const payloadCase = {
                ...filterPayload,
                clientId: row.data?.clientId,
                invoiceId: invoiceId,
                [paramName]: qmeDoctorContactId,
              };

              if (getChildUser) {
                const userDetails = await getChildUser(payloadCase);
                const level0Row = findRow(nestedRowsRef.current, row.parentId!);
                const level0Id =
                  level0Row?.id ||
                  row.parentId?.split("-").slice(0, 4).join("-");
                children = userDetails.map((user, index) => ({
                  id: `user-${qmeDoctorContactId}-${invoiceId}-${user.invoiceLineItemId || index}`,
                  level: 2,
                  isExpanded: false,
                  isLoading: false,
                  isLeaf: true,
                  parentId: row.id,
                  data: {
                    ...user,
                    patientName: user.patientName || "",
                    taskName: user.taskName || "",
                    noOfPages: user.noOfPages || "0",
                    noOfLines: user.noOfLines || "0",
                    rateType: user.rateType || "",
                    dateUploaded: user.dateUploaded || "",
                    rate: user.rate,
                    amount: user.amount,
                    invoiceStatus: user.invoiceStatus,
                    action: "View",
                    clientId: user.clientId,
                    qmeDoctorContactId: qmeDoctorContactId,
                    invoiceId: invoiceId,
                    mainParentId: level0Id,
                  },
                }));
              }
            }

            // Recursively expand children
            const expandedChildren = await expandAllRows(children);

            // Update the row with expanded children
            expandedRows[i] = {
              ...row,
              isExpanded: true,
              isLoading: false,
              children: expandedChildren,
            };
          } catch (error) {
            console.error("Failed to fetch child data:", error);
            expandedRows[i] = {
              ...row,
              isLoading: false,
              isExpanded: false,
            };
          }
        } else if (row.children && row.children.length > 0) {
          // Recursively expand children
          const expandedChildren = await expandAllRows(row.children);
          expandedRows[i] = {
            ...row,
            children: expandedChildren,
            isExpanded: true,
          };
        }
      }

      return expandedRows;
    };

    try {
      // Set loading state for all parent rows
      setNestedRows((prev) =>
        prev.map((row) => (row.level < 2 ? { ...row, isLoading: true } : row)),
      );

      // Expand all rows and get the fully expanded structure
      const fullyExpandedRows = await expandAllRows(nestedRows);
      setNestedRows(fullyExpandedRows);

      // Wait a moment for React to update the DOM
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Now get all eligible leaf IDs from the fully expanded structure
      const getAllEligibleLeafIdsFromAllRows = (rows: RowLevel[]): string[] => {
        let eligibleIds: string[] = [];

        const traverse = (currentRow: RowLevel) => {
          if (currentRow.level === 2 || currentRow.isLeaf) {
            if (isRowEligibleForSelection(currentRow.data)) {
              eligibleIds.push(currentRow.id);
            }
          }
          if (currentRow.children && currentRow.children?.length > 0) {
            currentRow.children.forEach((child) => traverse(child));
          }
        };

        rows.forEach((row) => traverse(row));
        return eligibleIds;
      };

      const allEligibleLeafIds =
        getAllEligibleLeafIdsFromAllRows(fullyExpandedRows);

      if (allEligibleLeafIds?.length === 0) {
        setSelectedRows([]);
        setSelectionUpdated(true);
        return;
      }

      // Check if all eligible rows are currently selected
      const currentlyAllSelected = allEligibleLeafIds.every((id) =>
        selectedRows.includes(id),
      );

      let newSelectedRows: string[];

      if (currentlyAllSelected) {
        // Deselect all
        newSelectedRows = [];
        setSelectedInvoiceId(null);
      } else {
        // Select all
        newSelectedRows = [...allEligibleLeafIds];
        // We don't set selectedInvoiceId here because multiple invoices would be selected
      }

      setSelectedRows(newSelectedRows);
      setSelectionUpdated(true);
    } catch (error) {
      console.error("Failed to select all rows:", error);
    } finally {
      // Clear loading states
      setNestedRows((prev) =>
        prev.map((row) => ({ ...row, isLoading: false })),
      );
    }
  }, [
    nestedRows,
    selectedRows,
    filterPayload,
    activeTab,
    getInvoiceByClientQme,
    getChildUser,
    isRowEligibleForSelection,
  ]);

  // Parent row checkbox (Level 0 & 1) - Select/Deselect all children
  const handleParentRowSelect = useCallback(
    async (row: RowLevel) => {
      if (row.level >= 2) return;

      const wasExpanded = row.isExpanded;

      try {
        setNestedRows((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, isLoading: true, isExpanded: true } : r,
          ),
        );

        // Function to load ALL children for a Level 0 row (Client)
        const loadAllLevel0Children = async (
          clientRow: RowLevel,
        ): Promise<RowLevel[]> => {
          const parts = clientRow.id.split("-");
          const clientId = parseInt(parts[1]);
          const invoiceId = parseInt(parts[3]);

          const clientPayload = {
            ...filterPayload,
            clientId: clientId,
            invoiceId: invoiceId,
          };

          if (!getInvoiceByClientQme) return [];

          const qmeDoctors = await getInvoiceByClientQme(clientPayload);
          const allLevel1Rows: RowLevel[] = [];

          // Load ALL Level 1 children (QME Doctors) with their Level 2 children
          for (const qme of qmeDoctors) {
            const level1Id = `qme-${qme.qmeDoctorContactId}-${invoiceId}`;

            // Check if this Level 1 row already exists
            const existingLevel1Row = findRow(nestedRowsRef.current, level1Id);

            let level1Row: RowLevel;

            if (
              existingLevel1Row &&
              existingLevel1Row.children &&
              existingLevel1Row.children.length > 0
            ) {
              // Level 1 row already has children loaded
              level1Row = { ...existingLevel1Row };
            } else {
              // Need to load Level 2 children for this QME doctor
              level1Row = {
                id: level1Id,
                level: 1,
                isExpanded: true,
                isLoading: false,
                parentId: clientRow.id,
                data: {
                  ...qme,
                  patientName: qme.qmeDoctorName,
                  noOfPages: qme.grandTotalNoOfPages,
                  noOfLines: qme.grandTotalNoOfLines,
                  amount: qme.grandTotalAmount,
                  totalAmount: qme.grandTotalAmount,
                  countOfCases: qme.countOfCases,
                  clientId: qme.clientId,
                  qmeDoctorContactId: qme.qmeDoctorContactId,
                  invoiceId: invoiceId,
                },
                children: [],
              };

              // Load ALL Level 2 children for this QME doctor
              const paramNameMap: Record<string, string> = {
                Client: "qmeDoctorContactId",
                Role: "billingForUserRole",
                User: "userId",
              };

              const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
              const payloadCase = {
                ...filterPayload,
                clientId: clientId,
                invoiceId: invoiceId,
                [paramName]: qme.qmeDoctorContactId,
              };

              if (getChildUser) {
                const userDetails = await getChildUser(payloadCase);
                level1Row.children = userDetails.map((user, index) => ({
                  id: `user-${qme.qmeDoctorContactId}-${invoiceId}-${user.invoiceLineItemId || index}`,
                  level: 2,
                  isExpanded: false,
                  isLoading: false,
                  isLeaf: true,
                  parentId: level1Id,
                  data: {
                    ...user,
                    patientName: user.patientName || "",
                    taskName: user.taskName || "",
                    noOfPages: user.noOfPages || "0",
                    noOfLines: user.noOfLines || "0",
                    rateType: user.rateType || "",
                    dateUploaded: user.dateUploaded || "",
                    rate: user.rate,
                    amount: user.amount,
                    invoiceStatus: user.invoiceStatus,
                    action: "View",
                    clientId: clientId,
                    qmeDoctorContactId: qme.qmeDoctorContactId,
                    invoiceId: invoiceId,
                    mainParentId: clientRow.id,
                  },
                }));
              }
            }

            level1Row.isExpanded = true;
            allLevel1Rows.push(level1Row);
          }

          return allLevel1Rows;
        };

        // Function to load ALL children for a Level 1 row (QME Doctor)
        const loadAllLevel1Children = async (
          qmeRow: RowLevel,
        ): Promise<RowLevel[]> => {
          const parts = qmeRow.id.split("-");
          const qmeDoctorContactId = parts[1];
          const invoiceId = parseInt(parts[2]);

          const paramNameMap: Record<string, string> = {
            Client: "qmeDoctorContactId",
            Role: "billingForUserRole",
            User: "userId",
          };

          const paramName = paramNameMap[activeTab] || "qmeDoctorContactId";
          const payloadCase = {
            ...filterPayload,
            clientId: qmeRow.data.clientId,
            invoiceId: invoiceId,
            [paramName]: qmeDoctorContactId,
          };

          if (!getChildUser) return [];
          const userDetails = await getChildUser(payloadCase);
          const level0Row = findRow(
            nestedRowsRef.current,
            qmeRow.mainParentId || qmeRow.parentId!,
          );
          const level0Id = level0Row?.id || qmeRow.mainParentId;
          return userDetails.map((user, index) => ({
            id: `user-${qmeDoctorContactId}-${invoiceId}-${user.invoiceLineItemId || index}`,
            level: 2,
            isExpanded: false,
            isLoading: false,
            isLeaf: true,
            parentId: qmeRow.id,
            data: {
              ...user,
              patientName: user.patientName || "",
              taskName: user.taskName || "",
              noOfPages: user.noOfPages || "0",
              noOfLines: user.noOfLines || "0",
              rateType: user.rateType || "",
              dateUploaded: user.dateUploaded || "",
              rate: user.rate,
              amount: user.amount,
              invoiceStatus: user.invoiceStatus,
              action: "View",
              clientId: qmeRow.data.clientId,
              qmeDoctorContactId: qmeDoctorContactId,
              invoiceId: invoiceId,
              mainParentId: level0Id,
            },
          }));
        };

        let allChildren: RowLevel[] = [];

        if (row.level === 0) {
          // Load ALL Level 1 rows with their Level 2 children
          allChildren = await loadAllLevel0Children(row);
        } else if (row.level === 1) {
          // Load ALL Level 2 children for this QME doctor
          allChildren = await loadAllLevel1Children(row);
        }

        // Update the state with all loaded children
        setNestedRows((prev) => {
          const updatedRows = updateRowChildren(prev, row.id, allChildren);

          // Return the updated rows
          return updatedRows.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  isLoading: false,
                  isExpanded: true,
                }
              : r,
          );
        });

        // Wait for state to update
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Get updated nested rows from ref
        const updatedNestedRows = nestedRowsRef.current;

        // Function to get ALL eligible leaf IDs from a row
        const getAllEligibleLeafIdsFromRow = (
          targetRow: RowLevel,
        ): string[] => {
          const targetRowInState =
            findRow(updatedNestedRows, targetRow.id) || targetRow;
          let leafIds: string[] = [];

          const traverse = (currentRow: RowLevel) => {
            if (currentRow.level === 2 || currentRow.isLeaf) {
              if (isRowEligibleForSelection(currentRow.data)) {
                leafIds.push(currentRow.id);
              }
            }
            if (currentRow.children && currentRow.children.length > 0) {
              currentRow.children.forEach((child) => traverse(child));
            }
          };

          traverse(targetRowInState);
          return leafIds;
        };

        // Get ALL eligible leaf IDs
        const eligibleLeafIds = getAllEligibleLeafIdsFromRow(row);

        // If no eligible leaves, return
        if (eligibleLeafIds.length === 0) {
          return;
        }

        // Check if ALL these leaf IDs are already selected
        const areAllLeavesSelected = eligibleLeafIds.every((id) =>
          selectedRows.includes(id),
        );

        let newSelectedRows: string[];

        if (areAllLeavesSelected) {
          // Deselect all these leaves
          newSelectedRows = selectedRows.filter(
            (id) => !eligibleLeafIds.includes(id),
          );

          // Check if there are any selected rows left
          if (newSelectedRows.length === 0) {
            setSelectedInvoiceId(null);
          } else {
            // Keep the existing selectedInvoiceId if there are still selected rows
          }
        } else {
          // Select all eligible leaves
          newSelectedRows = [...new Set([...selectedRows, ...eligibleLeafIds])];

          // Set the selected invoice ID from this parent row
          const invoiceId = getLevel0InvoiceId(row);
          if (invoiceId) {
            setSelectedInvoiceId(invoiceId);
          }
        }

        setSelectedRows(newSelectedRows);
        setSelectionUpdated(true);
      } catch (error) {
        console.error("Error selecting parent row:", error);
        setNestedRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? { ...r, isLoading: false, isExpanded: wasExpanded }
              : r,
          ),
        );
      }
    },
    [
      nestedRows,
      selectedRows,
      findRow,
      updateRowChildren,
      filterPayload,
      activeTab,
      getInvoiceByClientQme,
      getChildUser,
      isRowEligibleForSelection,
      onRowSelect,
      getLevel0InvoiceId,
    ],
  );

  const handleStatusLog = async (row: any) => {
    setShowLog(true);
    setInvoiceID(row);
  };

  const handleCloseStatusLog = async () => {
    setShowLog(false);
    setInvoiceID("");
  };
  // FIXED: Leaf row checkbox (Level 2) - Individual selection
  const handleLeafRowSelect = useCallback(
    (rowId: string, rowData: any, level: number, row: RowLevel) => {
      if (level < 2) return;

      // Check if row is eligible for selection
      if (!isRowEligibleForSelection(rowData)) {
        return;
      }

      setSelectedRows((prevSelectedRows) => {
        let newSelectedRows: string[];
        let newSelectedInvoiceId: number | null = selectedInvoiceId;

        if (prevSelectedRows.includes(rowId)) {
          // Deselect this row
          newSelectedRows = prevSelectedRows.filter((id) => id !== rowId);

          // If no rows left selected, clear the selected invoice ID
          if (newSelectedRows.length === 0) {
            newSelectedInvoiceId = null;
          }
        } else {
          // Select this row
          newSelectedRows = [...prevSelectedRows, rowId];

          // Set the selected invoice ID from this leaf row
          const invoiceId = getLevel0InvoiceId(row);
          if (invoiceId) {
            newSelectedInvoiceId = invoiceId;
          }
        }

        // Update selectedInvoiceId if changed
        if (newSelectedInvoiceId !== selectedInvoiceId) {
          setSelectedInvoiceId(newSelectedInvoiceId);
        }

        return newSelectedRows;
      });

      setSelectionUpdated(true);
    },
    [isRowEligibleForSelection, getLevel0InvoiceId, selectedInvoiceId],
  );

  const initializeNestedRowsWithFirstExpanded = useCallback(
    async (data: any[]): Promise<RowLevel[]> => {
      if (!data || data?.length === 0) return [];

      const initialNestedRows: RowLevel[] = [];

      for (let i = 0; i < data?.length; i++) {
        const client = data[i];
        const level0Id = `client-${client.clientId}-invoice-${client.invoiceId}-${client.invoiceInfoId}`;
        const isFirstClient = i === 0;

        const level0Row: RowLevel = {
          id: level0Id,
          level: 0,
          isExpanded: isFirstClient, // Only expand the first row
          isLoading: false, // Don't set loading true by default
          data: {
            ...client,
            patientName: client.clientName,
            invoiceGeneratedOn: client.invoiceGeneratedOn,
            noOfPages: client.grandTotalNoOfPages,
            noOfLines: client.grandTotalNoOfLines,
            amount: client.grandTotalAmount,
            totalAmount: client.grandTotalAmount,
            countOfQmeDoctors: client.countOfQmeDoctors,
            invoiceId: client.invoiceId,
          },
        };

        // Only fetch children if this is the first client and we want to expand it
        if (isFirstClient && getInvoiceByClientQme) {
          try {
            // Set loading state
            level0Row.isLoading = true;

            const clientPayload = {
              ...filterPayload,
              clientId: client.clientId,
              invoiceId: client.invoiceId,
            };

            const qmeDoctors = await getInvoiceByClientQme(clientPayload);

            // Create level 1 rows and expand the first one
            level0Row.children = await Promise.all(
              qmeDoctors.map(async (qme, qmeIndex) => {
                const isFirstQme = qmeIndex === 0;
                const level1Id = `qme-${qme.qmeDoctorContactId}-${client.invoiceId}`;

                const level1Row: RowLevel = {
                  id: level1Id,
                  level: 1,
                  isExpanded: isFirstQme,
                  isLoading: false,
                  parentId: level0Id,
                  data: {
                    ...qme,
                    patientName: qme.qmeDoctorName,
                    noOfPages: qme.grandTotalNoOfPages,
                    noOfLines: qme.grandTotalNoOfLines,
                    amount: qme.grandTotalAmount,
                    totalAmount: qme.grandTotalAmount,
                    countOfCases: qme.countOfCases,
                    clientId: qme.clientId,
                    qmeDoctorContactId: qme.qmeDoctorContactId,
                    invoiceId: client.invoiceId,
                  },
                };

                // If this is the first level 1 row, fetch its level 2 children
                if (isFirstQme && getChildUser) {
                  try {
                    level1Row.isLoading = true;

                    const paramNameMap: Record<string, string> = {
                      Client: "qmeDoctorContactId",
                      Role: "billingForUserRole",
                      User: "userId",
                    };

                    const paramName =
                      paramNameMap[activeTab] || "qmeDoctorContactId";
                    const payloadCase = {
                      ...filterPayload,
                      clientId: client.clientId,
                      invoiceId: client.invoiceId,
                      [paramName]: qme.qmeDoctorContactId,
                    };

                    const userDetails = await getChildUser(payloadCase);

                    level1Row.children = userDetails.map((user, userIndex) => ({
                      id: `user-${qme.qmeDoctorContactId}-${client.invoiceId}-${user.invoiceLineItemId || userIndex}`,
                      level: 2,
                      isExpanded: false,
                      isLoading: false,
                      isLeaf: true,
                      parentId: level1Id,
                      data: {
                        ...user,
                        patientName: user.patientName || "",
                        taskName: user.taskName || "",
                        noOfPages: user.noOfPages || "0",
                        noOfLines: user.noOfLines || "0",
                        rateType: user.rateType || "",
                        dateUploaded: user.dateUploaded || "",
                        rate: user.rate,
                        amount: user.amount,
                        invoiceStatus: user.invoiceStatus,
                        action: "View",
                        clientId: user.clientId,
                        qmeDoctorContactId: qme.qmeDoctorContactId,
                        invoiceId: client.invoiceId,
                        mainParentId: level0Id,
                      },
                    }));

                    level1Row.isLoading = false;
                  } catch (error) {
                    console.error(
                      "Failed to fetch level 2 data for first level 1 row:",
                      error,
                    );
                    level1Row.children = []; // Set empty children array
                    level1Row.isLoading = false;
                    level1Row.isExpanded = false;
                  }
                }

                return level1Row;
              }),
            );

            level0Row.isLoading = false;
          } catch (error) {
            console.error(
              "Failed to fetch level 1 data for first client:",
              error,
            );
            level0Row.children = []; // Set empty children array
            level0Row.isLoading = false;
            level0Row.isExpanded = false;
          }
        }

        initialNestedRows.push(level0Row);
      }

      return initialNestedRows;
    },
    [filterPayload, activeTab, getInvoiceByClientQme, getChildUser],
  );
  useEffect(() => {
    const hasDataChanged =
      JSON.stringify(prevClientInvoiceData.current) !==
      JSON.stringify(clientInvoiceData);

    if (isInitialMount.current || hasDataChanged) {
      let initialNestedRows: RowLevel[] = [];

      if (clientInvoiceData && clientInvoiceData?.length > 0) {
        setLoading(true);
        initializeNestedRowsWithFirstExpanded(clientInvoiceData)
          .then((rows) => {
            setNestedRows(rows);
          })
          .catch((error) => {
            console.error("Failed to initialize nested rows:", error);
            const fallbackRows = clientInvoiceData.map((client) => ({
              id: `client-${client.clientId}-invoice-${client.invoiceId}-${client.invoiceInfoId}`,
              level: 0,
              isExpanded: false,
              isLoading: false,
              data: {
                ...client,
                patientName: client.clientName,
                invoiceGeneratedOn: client.invoiceGeneratedOn,
                noOfPages: client.grandTotalNoOfPages,
                noOfLines: client.grandTotalNoOfLines,
                amount: client.grandTotalAmount,
                totalAmount: client.grandTotalAmount,
                countOfQmeDoctors: client.countOfQmeDoctors,
                invoiceId: client.invoiceId,
              },
            }));
            setNestedRows(fallbackRows);
          })
          .finally(() => {
            setLoading(false);
          });
      } else if (rows && rows?.length > 0) {
        const firstRowId = rows[0]?.id || `row-${Math.random()}`;
        initialNestedRows = rows.map((row, index) => ({
          id: row.id || `row-${Math.random()}`,
          level: 0,
          isExpanded: index === 0,
          isLoading: false,
          data: row,
        }));
        setNestedRows(initialNestedRows);
      }

      prevClientInvoiceData.current = clientInvoiceData;
    }

    isInitialMount.current = false;
  }, [clientInvoiceData, rows, initializeNestedRowsWithFirstExpanded]);

  // Sync external selectedRowIds
  useEffect(() => {
    const hasChanged =
      JSON.stringify(prevSelectedRowIds.current) !==
      JSON.stringify(selectedRowIds);

    if (hasChanged) {
      setSelectedRows(selectedRowIds || []);
      prevSelectedRowIds.current = selectedRowIds || [];

      // Update selectedInvoiceId based on the first selected row
      if (selectedRowIds && selectedRowIds.length > 0) {
        const firstSelectedId = selectedRowIds[0];
        const selectedRow = findRow(nestedRows, firstSelectedId);
        if (selectedRow) {
          const invoiceId = getLevel0InvoiceId(selectedRow);
          setSelectedInvoiceId(invoiceId);
        }
      } else {
        setSelectedInvoiceId(null);
      }
    }
  }, [selectedRowIds, nestedRows, findRow, getLevel0InvoiceId]);

  // Handle selection changes
  useEffect(() => {
    if (selectionUpdated && onRowSelect) {
      const selectedRowData = selectedRows
        .map((id) => {
          const row = findRow(nestedRows, id);
          return row ? row.data : null;
        })
        .filter(Boolean);

      onRowSelect({
        selectedRows: selectedRowData,
        selectedInvoiceId: selectedInvoiceId,
        nestedRows: nestedRows,
      });
      setSelectionUpdated(false);
    }
  }, [
    selectionUpdated,
    selectedRows,
    nestedRows,
    onRowSelect,
    findRow,
    selectedInvoiceId,
  ]);

  // FIXED: Check if all rows are selected
  const isAllSelected = useMemo(() => {
    if (nestedRows?.length === 0) return false;

    // Get all eligible leaf IDs
    const getAllEligibleLeafIdsFromAllRows = (rows: RowLevel[]): string[] => {
      let eligibleIds: string[] = [];

      const traverse = (currentRow: RowLevel) => {
        if (currentRow.level === 2 || currentRow.isLeaf) {
          if (isRowEligibleForSelection(currentRow.data)) {
            eligibleIds.push(currentRow.id);
          }
        }
        if (currentRow.children && currentRow.children.length > 0) {
          currentRow.children.forEach((child) => traverse(child));
        }
      };

      rows.forEach((row) => traverse(row));
      return eligibleIds;
    };

    const allEligibleLeafIds = getAllEligibleLeafIdsFromAllRows(nestedRows);

    if (allEligibleLeafIds.length === 0) return false;

    return allEligibleLeafIds.every((id) => selectedRows.includes(id));
  }, [nestedRows, selectedRows, isRowEligibleForSelection]);

  // FIXED: Check if some (but not all) rows are selected
  const isSomeSelected = useMemo(() => {
    if (nestedRows?.length === 0) return false;

    const getAllEligibleLeafIdsFromAllRows = (rows: RowLevel[]): string[] => {
      let eligibleIds: string[] = [];

      const traverse = (currentRow: RowLevel) => {
        if (currentRow.level === 2 || currentRow.isLeaf) {
          if (isRowEligibleForSelection(currentRow.data)) {
            eligibleIds.push(currentRow.id);
          }
        }
        if (currentRow.children && currentRow.children.length > 0) {
          currentRow.children.forEach((child) => traverse(child));
        }
      };

      rows.forEach((row) => traverse(row));
      return eligibleIds;
    };

    const allEligibleLeafIds = getAllEligibleLeafIdsFromAllRows(nestedRows);

    if (allEligibleLeafIds.length === 0) return false;

    const someSelected = allEligibleLeafIds.some((id) =>
      selectedRows.includes(id),
    );
    const allSelected = allEligibleLeafIds.every((id) =>
      selectedRows.includes(id),
    );

    return someSelected && !allSelected;
  }, [nestedRows, selectedRows, isRowEligibleForSelection]);

  const handleOpenModal = async (invoice: any, e: React.MouseEvent) => {
    setModalTitle("Details");
    const baseDetails = [
      { label: "Case ID", value: invoice.caseId },
      { label: "Patient", value: invoice.patientName },
      { label: "Doctor", value: invoice.qmeDoctorName },
      { label: "Priority", value: invoice?.priorityDesc || "" },
    ];
    const conditionalDetails =
      invoice.taskName !== "Standard Charge"
        ? [
            {
              label: "Case Exam Type",
              value: invoice?.appointmentTypeName || "",
            },
            {
              label: "Appointment Date & Time",
              value: invoice?.dateOfAppointmentFormat,
            },
          ]
        : [];

    setModalDetails([...baseDetails, ...conditionalDetails]);

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const handleInvoiceOpenModal = async (invoice: any, e: React.MouseEvent) => {
    setModalTitle("Details");
    let date = dateTimeFormatted(invoice.invoiceGeneratedOn);
    let generatedName = invoice.invGen_FullName;
    let generatedUserRole = invoice.invGen_UserRole;

    const monthDate = new Date(invoice.invoiceGeneratedOn);

    const billingPeriod = monthDate.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });

    let generatedDetails = `${date} | ${generatedName} (${generatedUserRole})`;
    setModalDetails([
      { label: "Invoice Number", value: invoice.invoiceInfoId },
      { label: "Client Name", value: invoice.patientName },
      { label: "Billing Period", value: billingPeriod },
      { label: "Invoice Status", value: invoice.invoiceStatus },
      {
        label: "Generated On | By",
        value: generatedDetails,
      },
    ]);

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const handleNotesView = async (invoice: any, e: React.MouseEvent) => {
    setModalTitle("Notes");
    const notesDetails = await getInvoiceCaseNotes(
      invoice.subjectQmeScheduleID,
      invoice.invoiceLineItemId,
    );
    setModalNotesData(notesDetails || []);

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const formatCurrency = useCallback((amount: number): string => {
    const number = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    return `$ ${number}`;
  }, []);

  const formatNumber = useCallback((num: number): string => {
    return new Intl.NumberFormat("en-US").format(num);
  }, []);

  const renderParentStats = useCallback(
    (row: RowLevel) => {
      if (row.level >= 2) return null;

      const rowData = row.data;

      const countColor = "#1565c0";
      const countBgColor = "#F4F8FE";
      const countBorderColor = "#90caf9";

      const pagesColor = "#1565c0";
      const pagesBgColor = "#F4F8FE";
      const pagesBorderColor = "#90caf9";

      const linesColor = "#1565c0";
      const linesBgColor = "#F4F8FE";
      const linesBorderColor = "#90caf9";

      return (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* ================= LEVEL 1 (QME) ================= */}
          {row.level === 1 && (
            <>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>{`Cases: ${rowData.countOfCases || 0}`}</Tooltip>
                }
              >
                <span
                  style={{
                    marginLeft: "auto",
                    minWidth: "90px",
                    textAlign: "center",
                    display: " inline-block",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor: countBgColor,
                    border: `1px solid ${countBorderColor}`,
                    fontSize: "12px",
                    fontWeight: "500",
                    color: countColor,
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "14px" }}>
                    cases
                  </span>
                  {rowData.countOfCases || 0}
                </span>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Total Pages:{" "}
                    {formatNumber(parseInt(rowData.noOfPages) || 0)}
                  </Tooltip>
                }
              >
                <span
                  style={{
                    minWidth: "90px",
                    textAlign: "center",
                    display: " inline-block",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor: pagesBgColor,
                    border: `1px solid ${pagesBorderColor}`,
                    fontSize: "12px",
                    fontWeight: "500",
                    color: pagesColor,
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "14px" }}>
                    auto_stories
                  </span>
                  {formatNumber(parseInt(rowData.noOfPages) || 0)}
                </span>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Total Lines:{" "}
                    {formatNumber(parseInt(rowData.noOfLines) || 0)}
                  </Tooltip>
                }
              >
                <span
                  style={{
                    minWidth: "90px",
                    textAlign: "center",
                    display: " inline-block",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    backgroundColor: linesBgColor,
                    border: `1px solid ${linesBorderColor}`,
                    fontSize: "12px",
                    fontWeight: "500",
                    color: linesColor,
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "14px" }}>
                    notes
                  </span>
                  {formatNumber(parseInt(rowData.noOfLines) || 0)}
                </span>
              </OverlayTrigger>
            </>
          )}

          {row.level === 0 && rowData.invoiceGeneratedOn && (
            <>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "12px",
                  color: "#555",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                }}
              >
                <span
                  className="material-symbols-outlined ms-2"
                  style={{
                    cursor: "pointer",
                    color: "#0092D5",
                    fontSize: "18px",
                  }}
                  onClick={(e) => handleStatusLog(rowData.invoiceId)}
                >
                  schedule
                </span>
                <strong>Generated on:</strong>
                <strong>{dateTimeFormatted(rowData.invoiceGeneratedOn)}</strong>
                <span
                  className="text-truncate"
                  style={{
                    marginLeft: "6px",
                    color: "#0092D5",
                    fontWeight: "600",
                    maxWidth: "235px",
                  }}
                >
                  | By {rowData.invGen_FullName} ({rowData.invGen_UserRole})
                </span>
              </span>
            </>
          )}

          <span
            style={{
              fontSize: "13px",
              fontWeight: "400",
              color: "#333",
              marginLeft: "8px",
              borderLeft: "1px solid #DFDADA",
              paddingLeft: "8px",
              minWidth: "135px",
              textAlign: "right",
            }}
          >
            Total&nbsp;
            <strong>
              {row.level === 0
                ? `${rowData?.currencySymbol ?? ""} ${Number(rowData?.totalInvoiceAmount)?.toFixed(2) ?? 0}`
                : row.level === 1
                  ? `${rowData?.currencySymbol ?? ""} ${Number(rowData?.totalAmount)?.toFixed(2) ?? 0}`
                  : null}
            </strong>
          </span>

          {row.level === 0 && rowData.invoiceGeneratedOn && (
            <>
              <span
                className="material-icons"
                onClick={() => onAction?.("View", row.data)}
                style={{
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#0098D7",
                }}
                title="Download"
              >
                download
              </span>
            </>
          )}
        </div>
      );
    },
    [formatNumber, formatCurrency, handleStatusLog, handleNotesView, onAction],
  );

  const renderRows = useCallback(
    (rows: RowLevel[]): JSX.Element[] => {
      return rows.flatMap((row) => {
        const hasNoChildren =
          row.isExpanded && (!row.children || row.children?.length === 0);

        const isAllSelected = areAllEligibleChildrenSelected(row);
        const isSomeSelected = isSomeChildrenSelected(row);

        // Check if checkbox should be disabled
        // Disable if there is a selected invoice AND this row is not under that invoice
        const isCheckboxDisabled =
          selectedInvoiceId !== null && !isRowUnderSelectedInvoice(row);

        // Special case for Level 0: also disable if another invoice is selected
        const isLevel0CheckboxDisabled =
          selectedInvoiceId !== null &&
          row.level === 0 &&
          row.data?.invoiceId !== selectedInvoiceId;

        const rowElement = (
          <tr
            key={row.id}
            className={
              row.level === 0
                ? "invoice_gridStructure"
                : "invoice_gridStructureInner"
            }
          >
            {headers.map((header, index) => {
              if (header.key === "patientName" && row.level === 0) {
                return (
                  <td key={header.key} colSpan={3}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "0px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          cursor: row.isLoading ? "default" : "pointer",
                          color: row.isLoading ? "#ccc" : "#0098d7",
                          fontSize: "20px",
                          marginRight: "6px",
                        }}
                        onClick={() =>
                          !row.isLoading &&
                          handleToggleExpand(row.id, row.level, row.data)
                        }
                      >
                        {row.isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : row.isExpanded ? (
                          "remove_circle"
                        ) : (
                          "add_circle"
                        )}
                      </span>
                      <input
                        key={`parent-checkbox-${row.id}-${row.children?.length || 0}-${row.isExpanded}`}
                        type="checkbox"
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = isSomeSelected;
                          }
                        }}
                        checked={isAllSelected}
                        onChange={() => handleParentRowSelect(row)}
                        disabled={
                          row.isLoading ||
                          isLevel0CheckboxDisabled ||
                          activeCardDisable
                        }
                        style={{
                          marginRight: "6px",
                          cursor:
                            row.isLoading || isLevel0CheckboxDisabled
                              ? "default"
                              : "pointer",
                        }}
                      />
                      <OverlayTrigger
                        placement="auto"
                        delay={{ show: 200, hide: 10 }}
                        overlay={
                          row.data.patientName ? (
                            <Tooltip>{row.data.patientName}</Tooltip>
                          ) : (
                            <></>
                          )
                        }
                      >
                        <span
                          className="text-truncate"
                          style={{ maxWidth: "300px" }}
                        >
                          {row.isLoading
                            ? "Loading..."
                            : `${row.data.invoiceInfoId} | ${row.data.patientName}`}
                        </span>
                      </OverlayTrigger>
                      <span
                        className="material-symbols-outlined ms-2"
                        style={{
                          cursor: "pointer",
                          color: "#f39c12",
                          fontSize: "18px",
                        }}
                        onClick={(e) => handleInvoiceOpenModal(row.data, e)}
                      >
                        info
                      </span>
                    </div>
                  </td>
                );
              }

              if (header.key === "patientName" && row.level === 1) {
                return (
                  <td key={header.key} colSpan={3}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "25px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          cursor: row.isLoading ? "default" : "pointer",
                          color: row.isLoading ? "#ccc" : "#0098d7",
                          fontSize: "20px",
                          marginRight: "6px",
                        }}
                        onClick={() =>
                          !row.isLoading &&
                          handleToggleExpand(row.id, row.level, row.data)
                        }
                      >
                        {row.isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : row.isExpanded ? (
                          "remove_circle"
                        ) : (
                          "add_circle"
                        )}
                      </span>
                      <input
                        key={`parent-checkbox-${row.id}-${row.children?.length || 0}-${row.isExpanded}`}
                        type="checkbox"
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = isSomeSelected;
                          }
                        }}
                        checked={isAllSelected}
                        onChange={() => handleParentRowSelect(row)}
                        disabled={
                          row.isLoading ||
                          isCheckboxDisabled ||
                          activeCardDisable
                        }
                        style={{
                          marginRight: "6px",
                          cursor:
                            row.isLoading || isCheckboxDisabled
                              ? "default"
                              : "pointer",
                        }}
                      />
                      <span>
                        {row.isLoading ? "Loading..." : row.data.patientName}
                      </span>
                    </div>
                  </td>
                );
              }

              if (header.key === "patientName" && row.level === 2) {
                const isEligible = isRowEligibleForSelection(row.data);
                const isDisabled = !isEligible || isCheckboxDisabled;

                return (
                  <td key={header.key} className="whitegurad">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        paddingLeft: "55px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() =>
                          handleLeafRowSelect(row.id, row.data, row.level, row)
                        }
                        disabled={isDisabled || activeCardDisable}
                        style={{
                          marginRight: "6px",
                          cursor: isDisabled ? "default" : "pointer",
                          opacity: isDisabled ? 0.5 : 1,
                          marginTop: "5px",
                        }}
                      />
                      <TruncateWithTooltip className="text-truncate truncatetwoline customDoc_line">
                        <span>{row.data.patientName}</span>
                      </TruncateWithTooltip>
                      <span
                        className="material-symbols-outlined ms-2"
                        style={{
                          cursor: "pointer",
                          color: "#f39c12",
                          fontSize: "18px",
                        }}
                        onClick={(e) => handleOpenModal(row.data, e)}
                      >
                        info
                      </span>
                    </div>
                  </td>
                );
              }

              if (row.level < 2 && header.key === "rate") {
                return (
                  <td key={header.key} colSpan={7}>
                    {renderParentStats(row)}
                  </td>
                );
              }

              if (
                row.level < 2 &&
                index > headers.findIndex((h) => h.key === "rate")
              ) {
                return null;
              }

              if (row.level === 2) {
                return (
                  <td
                    className="whitegurad"
                    key={header.key}
                    style={{
                      padding: header.isNumeric ? "8px 20px 8px 8px" : "8px",
                      textAlign: header.isNumeric
                        ? "center"
                        : header.key === "action"
                          ? "center"
                          : "left",
                    }}
                  >
                    {header.key === "dateUploaded" &&
                      (() => {
                        const [datePart, byPart] =
                          row.data.dateUploaded
                            ?.split("|")
                            .map((s: string) => s.trim()) || [];

                        return (
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span style={{ fontWeight: 500 }}>{datePart}</span>

                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-by-${row.id}`}>
                                  {byPart}
                                </Tooltip>
                              }
                            >
                              <span
                                className="d-inline-block text-truncate"
                                style={{
                                  fontSize: "13px",
                                  color: "#726d6d",
                                  maxWidth: "180px",
                                  cursor: "pointer",
                                }}
                              >
                                {byPart}
                              </span>
                            </OverlayTrigger>
                          </div>
                        );
                      })()}

                    {header.key === "rate" && (
                      <div
                        style={{
                          alignItems: "end",
                          gap: "6px",
                        }}
                      >
                        <span className="amountTxt">{row.data.rate}</span>
                      </div>
                    )}
                    {header.key === "amount" && (
                      <div
                        style={{
                          alignItems: "end",
                          gap: "6px",
                        }}
                      >
                        {row.data.lineItemType === "CustomLineItem" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                Custom Charge
                              </Tooltip>
                            }
                          >
                            <span className="customChargeBadge" />
                          </OverlayTrigger>
                        )}

                        <span className="amountTxt">{row.data.amount}</span>
                      </div>
                    )}

                    {header.key === "invoiceStatus" && (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          className={
                            row.data.invoiceStatus ===
                            INVOICE_STATUS.INVOICE_GENERATED
                              ? "Status_invoiceGenerated"
                              : row.data.invoiceStatus ===
                                  INVOICE_STATUS.INVOICE_AMENDED
                                ? "status_Exception_nobillingconfig"
                                : ""
                          }
                          style={{
                            fontWeight: "400",
                            lineHeight: "16px",
                            fontSize: "13px",
                          }}
                        >
                          {row.data.invoiceStatus}
                        </span>
                      </div>
                    )}

                    {header.key === "rateType" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {row.data?.billingRuleAppliedAt === "SYSTEM" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span className="rateType_System">S</span>
                          </OverlayTrigger>
                        )}

                        {row.data.billingRuleAppliedAt === "TEAM" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span className="rateType_Team">T</span>
                          </OverlayTrigger>
                        )}
                        {row.data.billingRuleAppliedAt === "CASE" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span className="rateType_Case">C</span>
                          </OverlayTrigger>
                        )}
                        {row.data.billingRuleAppliedAt === "EXCEPTION" && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-custom-charge-${row.id}`}>
                                {row.data?.billingRuleAppliedAt_Ttp}
                              </Tooltip>
                            }
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "14px",
                                height: "14px",
                                border: "2px solid #dc3545", // red border
                                color: "#dc3545", // icon color
                                borderRadius: "4px", // square with slight rounding
                              }}
                            >
                              <span
                                className="material-symbols-outlined"
                                style={{
                                  fontSize: "16px",
                                  lineHeight: 1,
                                }}
                              >
                                close_small
                              </span>
                            </span>
                          </OverlayTrigger>
                        )}
                        <span
                          style={{
                            fontWeight: "400",
                            lineHeight: "16px",
                            fontSize: "14px",
                            width: "75%",
                          }}
                        >
                          {row.data.rateType}
                        </span>
                      </div>
                    )}

                    {header.key !== "action" &&
                      header.key !== "dateUploaded" &&
                      header.key !== "amount" &&
                      header.key !== "rate" &&
                      header.key !== "rateType" &&
                      header.key !== "invoiceStatus" && (
                        <OverlayTrigger
                          placement="auto"
                          delay={{ show: 200, hide: 10 }}
                          overlay={
                            row.data[header.key]?.length > 15 ? (
                              <Tooltip>{row.data[header.key]}</Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className="truncatetwoline">
                            {row.data[header.key]}
                          </span>
                        </OverlayTrigger>
                      )}
                  </td>
                );
              }

              return null;
            })}
          </tr>
        );

        if (hasNoChildren && !row.isLoading) {
          return [
            rowElement,
            <tr key={`${row.id}-no-records`}>
              <td
                colSpan={headers.length}
                style={{
                  textAlign: "center",
                  paddingLeft: `${(row.level + 1) * 30}px`,
                }}
              >
                <span className="no_rec_data">No Record Found</span>
              </td>
            </tr>,
          ];
        }

        const childElements =
          row.isExpanded && row.children?.length
            ? renderRows(row.children)
            : [];

        return [rowElement, ...childElements];
      });
    },
    [
      headers,
      selectedRows,
      disabled,
      isLogTable,
      handleToggleExpand,
      handleParentRowSelect,
      handleLeafRowSelect,
      onAction,
      renderParentStats,
      areAllEligibleChildrenSelected,
      isSomeChildrenSelected,
      isRowEligibleForSelection,
      activeTab,
      selectedInvoiceId,
      isRowUnderSelectedInvoice,
    ],
  );

  const renderedRows = useMemo(
    () => renderRows(nestedRows),
    [nestedRows, renderRows],
  );

  const isDetails = modalTitle === "Details";
  const modalProps = isDetails
    ? {
        mode: "details" as const,
        details: modalDetails,
      }
    : {
        mode: "table" as const,
        data: modalNotesData,
        headers: INVOICE_NOTES_INFO_HEADER,
      };

  return (
    <>
      <Loader isLoading={loading} />
      <div
        className="table-responsive border-0 mb-2 invoicegridss"
        style={{ maxHeight: "unset !important", display: "contents" }}
      >
        <Table className="table-hover" style={{ tableLayout: "fixed" }}>
          <thead className="manageInvoiceStickyHeader">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={header.key}
                  style={{
                    border: "none",
                    padding: "8px",
                    backgroundColor: "#e0f7fa",
                    color: "#000",
                    cursor: header.sortable ? "pointer" : "default",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    textAlign: header.key === "action" ? "center" : "left",
                    ...header.style,
                  }}
                >
                  {index === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span>{header.label}</span>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          header.key === "action" ? "center" : "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>{header.label}</span>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {nestedRows?.length > 0 ? (
              renderedRows
            ) : (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: "center" }}>
                  <span className="no_rec_data">No Record Found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <FloatingTableModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        position={modalPos}
        {...modalProps}
      />
      {showLog && (
        <InvoiceReportsLogs
          show={showLog}
          handleClose={handleCloseStatusLog}
          caseInfoId={invoiceID}
        />
      )}
    </>
  );
};

export default InvoiceReportDataTable;
